import { type DokumentInput } from '~/gql/ucpw/graphql';
import { useAsset, useSnippetId, useViewer } from '~/hooks';
import { usePdfs } from '~/hooks/usePdfs';
import { usePdf } from '~/pages/projects/hooks/usePdf';
import type { PxQuestion } from '~/meta/data/px-connect';
import { ProjectIds } from '~/pages/projects/types';
import { assertPdfResult, reuploadPdf, type PdfResult } from './pxAttachments';
import log from '~/log';

interface CreateArgs {
    questions: PxQuestion[];
    schadensobjekt?: string;
    schadenNr?: string;
    reportId?: number;
    /** The Einsatzbericht row the modal was opened from — drives the report PDF. */
    report?: any;
    /** Attach the Fragenkatalog PDF (default true). */
    attachFragenkatalog?: boolean;
    /** Attach the Leckortungsbericht PDF (default true). */
    attachBericht?: boolean;
}

type PreparedCorrespondenceDocument = {
    dokument: {
        name: string;
        pfad: string;
        beschreibung: string;
        uploadDatum: Date;
        typSnippet: {
            value: number;
            label: string;
        };
        mimeType: string;
    };
};

/**
 * Prepare a PX Connect Schadenbericht Korrespondenz draft (UCPWO-378),
 * attaching selected generated PDFs: the Fragenkatalog and/or the Leckortungsbericht.
 *
 * Die Korrespondenz wird als echter PX-Connect-Typ vorbereitet: Typ „PX Connect
 * Nachricht" (Kürzel PXC) + Subtyp „Schadenbericht". Das Fragenkatalog-PDF
 * (px-connect-schadenbericht.pdf) bekommt den Dokumenttyp „Schadenaufnahme"; ein
 * optional angehängter Leckortungsbericht bleibt „Sonstiges".
 *
 * Mirrors the house pattern (CoverLetterModal → useDocuments.createDocumentFromSignedUrl):
 * each PDF is generated → fetched from its presigned URL → re-uploaded into the
 * documents bucket via `useAsset().upload` (so the attachment key resolves like any
 * uploaded document) → then passed into the standard Korrespondenz modal for review.
 * Die IDs werden nur gesetzt; der SnippetSelect im Korrespondenz-Modal löst sie zur
 * vollen Option (inkl. key 'PXC') auf, wodurch Subtyp-Feld + PXC-Pfad greifen.
 */
export const usePxConnectKorrespondenz = ({ projectId, subprojectId }: ProjectIds) => {
    const { getPdf } = usePdfs();
    const { leakDetectionReport } = usePdf({ projectId, subprojectId });
    const { upload } = useAsset();
    const viewer = useViewer();
    const mitarbeiterId = viewer?.app?.mitarbeiterId;
    // Korrespondenz-Typ „PX Connect Nachricht" (Kürzel PXC) + PX-Subtyp „Schadenbericht".
    const { typSnippetId: korrespondenzTypSnippetId } = useSnippetId({
        name: 'PX Connect Nachricht',
        category: 'Korrespondenz',
        property: 'Typ',
    });
    // PX-Subtyp „Schadenbericht" (Kürzel SB, siehe PXC_SUBTYP_SCHADENBERICHT_KUERZEL).
    const { typSnippetId: subtypSnippetId } = useSnippetId({
        name: 'Schadenbericht',
        category: 'Korrespondenz',
        property: 'PXSubtyp',
    });
    // Dokumenttyp des Fragenkatalog-PDFs: „Schadenaufnahme"; der Leckortungsbericht: „Ortungsbericht".
    const { typSnippetId: schadenaufnahmeTypSnippetId, typSnippet: schadenaufnahmeTypSnippet } =
        useSnippetId({
            name: 'Schadenaufnahme',
            category: 'Dokument',
            property: 'Typ',
        });
    const { typSnippetId: ortungsberichtTypSnippetId, typSnippet: ortungsberichtTypSnippet } =
        useSnippetId({
            name: 'Ortungsbericht',
            category: 'Dokument',
            property: 'Typ',
        });

    const buildDocuments = async ({
        questions,
        schadensobjekt,
        schadenNr,
        reportId,
        report,
        attachFragenkatalog = true,
        attachBericht = false,
    }: CreateArgs): Promise<DokumentInput[]> => {
        // 1) Generate the selected PDFs (returns presigned URLs, no download).
        const [katalogPdf, berichtPdf] = await Promise.all([
            attachFragenkatalog
                ? getPdf({
                      template: 'pxConnectSchadenbericht',
                      filename: 'px-connect-schadenbericht.pdf',
                      data: { questions, reportId, schadensobjekt, schadenNr },
                  })
                : Promise.resolve(undefined),
            attachBericht
                ? (leakDetectionReport({ report }, 'get') as Promise<{
                      signedUrl?: string;
                      error?: string;
                  }>)
                : Promise.resolve(undefined),
        ]);

        // 2) Validate, download and re-upload each requested PDF in parallel.
        // A requested PDF that fails (generation error, expired URL, failed
        // upload) rejects here and aborts the submit — no partial Korrespondenz.
        // Jeder Anhang trägt seinen eigenen Dokumenttyp (Fragenkatalog →
        // „Schadenaufnahme", Leckortungsbericht → „Ortungsbericht").
        const buildDocument = async (
            result: PdfResult | undefined,
            fileName: string,
            label: string,
            typSnippetId: number
        ): Promise<DokumentInput> => {
            const signedUrl = assertPdfResult(result, label);
            const { key, name } = await reuploadPdf(upload, signedUrl, fileName);
            return {
                name,
                pfad: key,
                beschreibung: label,
                subprojektId: subprojectId as number,
                typSnippetId,
                uploadDatum: new Date(),
            };
        };

        const documents: DokumentInput[] = (
            await Promise.all([
                attachFragenkatalog
                    ? buildDocument(
                          katalogPdf,
                          'px-connect-schadenbericht.pdf',
                          'PX Connect Schadenbericht',
                          schadenaufnahmeTypSnippetId
                      )
                    : Promise.resolve(null),
                attachBericht
                    ? buildDocument(
                          berichtPdf,
                          'ortungsbericht.pdf',
                          'Leckortungsbericht',
                          ortungsberichtTypSnippetId
                      )
                    : Promise.resolve(null),
            ])
        ).filter((doc): doc is DokumentInput => doc !== null);

        return documents;
    };

    // Anzeigelabel je Dokumenttyp-Id (für die typSnippet-Option im Korrespondenz-Modal).
    const labelForDocType = (id?: number) =>
        id === schadenaufnahmeTypSnippetId
            ? schadenaufnahmeTypSnippet?.name || 'Schadenaufnahme'
            : ortungsberichtTypSnippet?.name || 'Ortungsbericht';

    const subjectFor = (schadenNr?: string) =>
        ['PX Connect Schadenbericht', schadenNr && `Schaden-Nr. ${schadenNr}`]
            .filter(Boolean)
            .join(' – ');

    const prepareKorrespondenzDraft = async (args: CreateArgs) => {
        // useSnippetId yields 0 until the snippet query resolves; never prepare with a bogus type.
        if (!korrespondenzTypSnippetId || !subtypSnippetId || !schadenaufnahmeTypSnippetId) {
            throw new Error(
                'PX-Connect-Snippets („PX Connect Nachricht"/„Schadenbericht"/„Schadenaufnahme") sind noch nicht geladen.'
            );
        }

        const documents = await buildDocuments(args);
        const preparedDocuments: PreparedCorrespondenceDocument[] = documents.map((document) => ({
            dokument: {
                name: document.name || '',
                pfad: document.pfad || '',
                beschreibung: document.beschreibung || '',
                uploadDatum: document.uploadDatum || new Date(),
                typSnippet: {
                    value: document.typSnippetId as number,
                    label: labelForDocType(document.typSnippetId as number),
                },
                mimeType: 'application/pdf',
            },
        }));
        const subject = subjectFor(args.schadenNr);
        const correspondence = {
            subprojektId: subprojectId as number,
            typSnippetId: korrespondenzTypSnippetId,
            subtypSnippetId,
            korrespondenzDokument: preparedDocuments,
            freitext: JSON.stringify([{ type: 'paragraph', children: [{ text: subject }] }]),
            mitarbeiterIdSachbearbeiter: mitarbeiterId,
            mitarbeiterIdUrheber: mitarbeiterId,
            zeit: new Date(),
            beteiligter: ' ',
        };

        log.debug('usePxConnectKorrespondenz.prepare', { correspondence });
        return correspondence;
    };

    return {
        prepareKorrespondenzDraft,
        ready: Boolean(
            korrespondenzTypSnippetId &&
                subtypSnippetId &&
                schadenaufnahmeTypSnippetId &&
                ortungsberichtTypSnippetId &&
                subprojectId
        ),
    };
};
